import OpeningHours from 'opening_hours'

import type {DealerSchedule, DealerScheduleDay, TimeRange} from '@/types/microfrontend'

const WEEKDAY_KEYS: DealerScheduleDay[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

const MINUTES_IN_DAY = 24 * 60

function toHHmm(minutes: number): string {
  const clamped = Math.max(0, Math.min(MINUTES_IN_DAY, minutes))
  const h = Math.floor(clamped / 60) % 24
  const m = clamped % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

function minutesSinceMidnight(date: Date, reference: Date): number {
  const diffMs = date.getTime() - reference.getTime()
  const diffMin = Math.round(diffMs / 60000)
  return Math.max(0, Math.min(MINUTES_IN_DAY, diffMin))
}

function mergeRanges(ranges: TimeRange[]): TimeRange[] {
  if (ranges.length <= 1) return ranges
  const sorted = [...ranges].sort((a, b) => a.start.localeCompare(b.start))
  const result: TimeRange[] = [sorted[0]]
  for (let i = 1; i < sorted.length; i++) {
    const last = result[result.length - 1]
    const current = sorted[i]
    if (current.start <= last.end) {
      result[result.length - 1] = {start: last.start, end: current.end > last.end ? current.end : last.end}
    } else {
      result.push(current)
    }
  }
  return result
}

/**
 * Parses an array of OSM opening_hours rules (e.g. `"Mo-Fr 10:00-13:00"`)
 * into a schedule keyed by weekday, with each day holding zero or more
 * `{start, end}` ranges in `HH:mm` format.
 */
export function parseDealerSchedule(openingHours: string[] | undefined | null): DealerSchedule | null {
  if (!openingHours || openingHours.length === 0) return null

  const joined = openingHours.filter(Boolean).join(', ')
  if (!joined) return null

  let oh: OpeningHours
  try {
    oh = new OpeningHours(joined)
  } catch (err) {
    console.warn('parseDealerSchedule: invalid opening_hours rule', joined, err)
    return null
  }

  const schedule: DealerSchedule = {
    sunday: [],
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
  }

  // Probe a single ISO week (Mon..Sun) to extract each day's intervals.
  const probeMonday = new Date(2024, 0, 1) // 2024-01-01 is a Monday, local time
  for (let offset = 0; offset < 7; offset++) {
    const dayStart = new Date(probeMonday)
    dayStart.setDate(probeMonday.getDate() + offset)
    dayStart.setHours(0, 0, 0, 0)
    const dayEnd = new Date(dayStart)
    dayEnd.setHours(23, 59, 59, 999)

    const intervals = oh.getOpenIntervals(dayStart, dayEnd)
    const ranges: TimeRange[] = intervals.map(interval => {
      const [from, to] = interval
      return {
        start: toHHmm(minutesSinceMidnight(from, dayStart)),
        end: toHHmm(minutesSinceMidnight(to, dayStart)),
      }
    })

    const weekdayKey = WEEKDAY_KEYS[dayStart.getDay()]
    schedule[weekdayKey] = mergeRanges(ranges)
  }

  return schedule
}
